import { Component, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from '../../i18n/i18n';

function ErrorFallback() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-dvh items-center justify-center w-full">
      <div className="flex flex-col items-center gap-4 text-center max-w-lg w-full">
        <div className="flex items-center justify-center size-12 rounded-full bg-red-100">
          <FontAwesomeIcon
            icon={faCircleExclamation}
            className="size-7 text-destructive"
          />
        </div>
        <h2 className="text-2xl font-semibold text-foreground">
          {t('abort.serverError.title')}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t('abort.serverError.subtitle')}
        </p>
      </div>
    </div>
  );
}

type Props = {
  children: ReactNode;
  /** Shown instead of the hosted error page. */
  fallback?: ReactNode;
  /** Called once when a child crashes. */
  onError?: () => void;
};

type State = {
  hasError: boolean;
};

export default class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch() {
    this.props.onError?.();
  }

  override render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <ErrorFallback />;
    }
    return this.props.children;
  }
}
